import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import SearchInput from '@components/shared/SearchInput/SearchInput';
import { useSearchLazyQuery } from '@graphql/generated';
import useChatInput from '@hooks/useChatInput';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import useGlobalSearchProject from '@hooks/useGlobalSearchProjects';

interface MyTagModalHeaderProps {
  filterVal: string;
  onChangeFilterVal: (newFilterVal: string) => void;
  isTagSearch?: boolean;
  isEditTagSearch?: boolean;
  onCancelPress?: () => void;
}
export const MyTagModalHeader: React.FC<MyTagModalHeaderProps> = ({
  filterVal,
  onChangeFilterVal,
  isTagSearch,
  isEditTagSearch,
  onCancelPress,
}) => {
  const { t } = useTranslation();
  const { setIsTagModalOpen } = useChatInput();

  const {
    search,
    setSearch,
    storeRecentSearchTerm,
    setIsSearching,
    setViewAllEvent,
  } = useGlobalSearchProject();

  useEffect(() => {
    return () => {
      setSearch('');
      setIsSearching(false);
      setViewAllEvent(true);
    };
  }, []);

  const [globalSearch] = useSearchLazyQuery();

  // Results are not consumed by this component — kept query-agnostic and
  // gated the same as every other Wave 3 site so nothing here fires below
  // the shared 3-char/300ms threshold (TA-10167).
  const handleSearch = useCallback(
    (term: string) => {
      globalSearch({
        variables: {
          term,
          size: 20,
          includeMessages: false,
          includeDocuments: false,
          includeProjects: true,
          includeTasks: true,
        },
      });
    },
    [globalSearch]
  );
  useDebouncedSearch(search, handleSearch);

  return (
    <>
      <Box
        paddingHorizontal='m'
        paddingBottom='m'
        borderBottomColor='grey02'
        marginTop='xxs'
        borderBottomWidth={1}>
        <Box marginTop='m' flexDirection='row' alignItems='center'>
          <SearchInput
            onTextChanged={(v) => {
              setSearch(v);
              onChangeFilterVal(v);
            }}
            value={filterVal}
            autoFocus
            isTagSearch={isTagSearch}
            isEditTagSearch={isEditTagSearch}
            onBlur={() => {
              storeRecentSearchTerm();
            }}
          />
          <TouchableOpacity
            onPress={() => {
              setIsTagModalOpen(false);
              onCancelPress?.();
            }}>
            <Text marginLeft='xs' variant='buttonLabel' color='greenSecondary'>
              {t('shared:cancel')}
            </Text>
          </TouchableOpacity>
        </Box>
      </Box>
    </>
  );
};
